import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

export const ProcessFlow = ({ steps, className = '' }) => {
  return (
    <div className={`relative ${className}`}>
      {/* Connection Line */}
      <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-sky-200 via-sky-400 to-sky-200 dark:from-sky-800 dark:via-sky-600 dark:to-sky-800 -translate-y-1/2 z-0" />
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
        {steps.map((step, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.15 }}
            className="relative"
          >
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-100 dark:border-slate-700 hover:shadow-lg transition-shadow duration-300">
              {/* Step Number */}
              <div className="w-12 h-12 rounded-full bg-sky-500 text-white flex items-center justify-center text-lg font-bold mb-4 shadow-lg shadow-sky-500/30">
                {index + 1}
              </div>
              
              {/* Icon */}
              {step.icon && (
                <div className="w-10 h-10 rounded-lg bg-sky-50 dark:bg-sky-900/50 flex items-center justify-center mb-4">
                  <step.icon className="w-5 h-5 text-sky-600 dark:text-sky-400" />
                </div>
              )}
              
              <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                {step.title}
              </h4>
              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                {step.description}
              </p>
            </div>
            
            {/* Arrow (hidden on last item) */}
            {index < steps.length - 1 && (
              <div className="hidden lg:flex absolute top-1/2 -right-4 -translate-y-1/2 z-20">
                <div className="w-8 h-8 rounded-full bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 flex items-center justify-center shadow-sm">
                  <ArrowRight className="w-4 h-4 text-sky-500" />
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export const ProcessFlowVertical = ({ steps, className = '' }) => {
  return (
    <div className={`relative ${className}`}>
      {/* Vertical Line */}
      <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-sky-200 via-sky-400 to-sky-200 dark:from-sky-800 dark:via-sky-600 dark:to-sky-800" />
      
      <div className="space-y-8">
        {steps.map((step, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.15 }}
            className="relative pl-16"
          >
            {/* Step Number */}
            <div className="absolute left-0 top-0 w-12 h-12 rounded-full bg-sky-500 text-white flex items-center justify-center text-lg font-bold shadow-lg shadow-sky-500/30 z-10">
              {index + 1}
            </div>
            
            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm border border-slate-100 dark:border-slate-700 hover:shadow-md transition-shadow duration-300">
              <h4 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                {step.title}
              </h4>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                {step.description}
              </p>
              {step.features && (
                <ul className="mt-4 space-y-2">
                  {step.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                      <div className="w-1.5 h-1.5 rounded-full bg-sky-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
